'use client';

import { useEffect, useRef } from 'react';
import { api } from '@/lib/api';
import { useAuthStore } from '@/lib/auth.store';
import { useAdminAuthStore } from '@/lib/admin-auth.store';
import { getPlatformToken } from '@/lib/platform-session';
import { getSocket } from '@/lib/socket';
import { performClientLogout } from '@/lib/session-client';
import { isAdminPath } from '@/lib/admin-paths';

const HEARTBEAT_INTERVAL_MS = 60_000;
/** When a session kick arrives, re-ask this many times before believing it. See handleForceLogout. */
const SESSION_RECHECK_DELAYS_MS = [0, 1_000, 2_500];

export function SessionManager() {
  const platformToken = useAuthStore(s => s.token);
  const adminToken = useAdminAuthStore(s => s.token);
  const lastHeartbeatAtRef = useRef(0);
  const heartbeatInFlightRef = useRef(false);

  const resolveActiveToken = () => {
    const adminStoreToken = useAdminAuthStore.getState().token;
    const platformStoreToken = getPlatformToken();
    return isAdminPath(window.location.pathname)
      ? adminStoreToken || platformStoreToken
      : platformStoreToken || adminStoreToken;
  };

  useEffect(() => {
    const token = resolveActiveToken();
    if (!token) {
      // Downgrade to a PUBLIC socket — do not tear the socket down (owner, 2026-09-08).
      //
      // `disconnectSocket()` here was why the header ticker crawled for logged-out visitors while
      // logged-in ones saw it fly. React runs child effects before parent ones, so the ticker
      // (deep in the tree) called `getSocket()` and subscribed, and then THIS effect — in
      // `Providers`, at the top — destroyed the very socket it had just subscribed to. Traced on
      // staging: two sockets opened and closed with 0 ticks each before a third happened to
      // survive. Whether a later mount re-created one was pure timing, so it worked on a fast
      // machine and lost the race on a real one. When it lost, `useMarketWatch` fell back to its
      // REST refetch — and because the prices are all present from the server-side seed, that is
      // the SLOW interval: measured 15 seconds between updates.
      //
      // `getSocket()` with no token is not a weaker teardown. It sets `auth` to `{}` and
      // reconnects, so an authenticated connection is genuinely dropped and replaced by an
      // anonymous one; server-side `handleConnection` then marks it `publicOnly` and never joins
      // the `user:` room, so no private data can reach it. The index-price broadcast is public and
      // must keep flowing in every mode — that is the whole point of the ticker.
      getSocket();
      return;
    }

    const socket = getSocket(token);
    // A force-logout says "the session THIS SOCKET belongs to is over" — which is not always the
    // session the browser is holding now. Every login kicks the sockets of the session it
    // replaces, so a socket left over from a previous attempt, or from a second tab, delivers a
    // kick a moment after a successful login. Acting on it blindly logged the browser out of the
    // session it had just created, and the admin login page came straight back — every retry
    // leaving another socket behind for the next attempt to kill (2026-09-07).
    //
    // So ask the server whether THIS browser is still authenticated, and ask more than once. The
    // kick is sent while the login that caused it is still being answered, so for a moment the
    // browser is still holding the OLD cookie and a single probe comes back 401 — measured on
    // staging, where one probe bounced the page anyway. A few seconds later the new cookie has
    // landed and the same probe succeeds. Only a session that refuses us every time is really
    // over (a login on another device, an admin revoking it, an idle timeout); a network error is
    // not a refusal, and staying logged in is the right answer to it.
    let abandoned = false;
    const stillAuthenticated = async (): Promise<boolean> => {
      for (const waitMs of SESSION_RECHECK_DELAYS_MS) {
        if (waitMs) await new Promise(resolve => setTimeout(resolve, waitMs));
        if (abandoned) return true;
        try {
          await api.post('/auth/heartbeat');
          return true;
        } catch (error: unknown) {
          const status = (error as { response?: { status?: number } })?.response?.status;
          if (status !== 401 && status !== 403) return true;
        }
      }
      return false;
    };

    const handleForceLogout = () => {
      void stillAuthenticated().then(valid => {
        if (!valid && !abandoned) void performClientLogout();
      });
    };

    socket.on('force-logout', handleForceLogout);

    return () => {
      abandoned = true;
      socket.off('force-logout', handleForceLogout);
    };
  }, [adminToken, platformToken]);

  useEffect(() => {
    const sendHeartbeat = () => {
      const token = resolveActiveToken();
      if (!token || heartbeatInFlightRef.current) {
        return;
      }

      const now = Date.now();
      if (now - lastHeartbeatAtRef.current < HEARTBEAT_INTERVAL_MS) {
        return;
      }

      lastHeartbeatAtRef.current = now;
      heartbeatInFlightRef.current = true;

      api.post('/auth/heartbeat')
        .catch(() => undefined)
        .finally(() => {
          heartbeatInFlightRef.current = false;
        });
    };

    const interval = window.setInterval(sendHeartbeat, 15_000);
    const handleVisibilityOrFocus = () => {
      if (document.visibilityState === 'visible') {
        lastHeartbeatAtRef.current = 0;
        sendHeartbeat();
      }
    };

    window.addEventListener('focus', handleVisibilityOrFocus);
    document.addEventListener('visibilitychange', handleVisibilityOrFocus);

    sendHeartbeat();

    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', handleVisibilityOrFocus);
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
    };
  }, []);

  return null;
}
